import SearchIcon from '@mui/icons-material/Search';
import { 
    Box, 
    Autocomplete, 
    TextField, 
    useTheme, 
    Typography, 
    Divider,
    AutocompleteChangeReason, 
    AutocompleteChangeDetails,
    AutocompleteInputChangeReason 
} from '@mui/material';
import React, { useEffect, useState, useMemo, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransaction } from '../../../context/TransactionContext';
import { TransactionTypeValue } from '../../../services/transaction.types';
import { format, parse, isValid } from 'date-fns';
import { FilterValues, FilterKeys, FilterUpdatePayload, FilterInstructionAction } from '../../../types/filter.types';

export enum FilterCategory {
    TYPE = 'TYPE',
    ACCOUNT = 'ACCOUNT',
    ASSET = 'ASSET',
    FLAG = 'FLAG',
    DATE = 'DATE',
    TEXT = 'TEXT',
    // Subcategories
    DATE_BEFORE = 'DATE_BEFORE',
    DATE_AFTER = 'DATE_AFTER',  
    DATE_EXACT = 'DATE_EXACT',
}

export interface SearchOption {
    category: FilterCategory;
    label: string;
    value: string | number | TransactionTypeValue | Date; 
    subcategory?: FilterCategory; 
    categoryLabel?: string; 
    icon?: React.ReactNode;
}

interface SearchProps {
    onFiltersChange: (filters: FilterUpdatePayload) => void;
    value?: string;
    onDropdownStateChange?: (isOpen: boolean) => void;
    activeFilters?: FilterValues; 
}

const Search: React.FC<SearchProps> = ({ onFiltersChange, value = '', onDropdownStateChange, activeFilters = {} }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { userTypes: transactionTypes, transactionIssues, accounts, userCoins: assets } = useTransaction();
    const [inputValue, setInputValue] = useState<string>(value || activeFilters.search || ''); 
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    
    const tryParseDate = (dateText: string): Date | null => {
        dateText = dateText.trim();
        if (!dateText) return null;
        
        // 1. Explicitly try formats with 2-digit years first using date-fns parse
        const formatsWithTwoDigitYear = ['dd/MM/yy', 'MM/dd/yy', 'dd.MM.yy'];
        const referenceDate = new Date(); // Use today to resolve yy ambiguity (e.g., 94 -> 1994, 23 -> 2023)
        for (const formatStr of formatsWithTwoDigitYear) {
            try {
                const parsed = parse(dateText, formatStr, referenceDate);
                if (isValid(parsed) && parsed.getFullYear() > 1900) { // Ensure year is reasonable

                    return parsed;
                }
            } catch (e) {
                // Ignore parsing errors, try next format
            }
        }
        
        // 2. Handle single number with trailing dot (e.g., "1.")
        if (dateText.match(/^\d{1,2}\.$/)) {
            const dayOfMonth = parseInt(dateText.slice(0, -1), 10);

            // Extract just the number and treat as a day
            const today = new Date();
            if (dayOfMonth >= 1 && dayOfMonth <= 31) {
                const date = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);

                if (isValid(date)) {
                    return date;
                }
            }
        }
        
        // Basic date validation and normalization
        const normalizeShorthandYear = (input: string): string => {
            // Match patterns like M.D.YY or M/D/YY or M-D-YY
            const shorthandRegex = /^(\d{1,2})[\.\/-](\d{1,2})[\.\/-](\d{2})$/;
            if (shorthandRegex.test(input)) {
                const parts = input.split(/[\.\/-]/);
                if (parts.length === 3) {
                    const month = parts[0].padStart(2, '0');
                    const day = parts[1].padStart(2, '0');
                    // Convert 2-digit year to 4-digit
                    const yearNum = parseInt(parts[2], 10);
                    const fullYear = yearNum < 50 ? `20${parts[2].padStart(2, '0')}` : `19${parts[2].padStart(2, '0')}`;
                    return `${month}/${day}/${fullYear}`; // Return in MM/DD/YYYY format
                }
            }
            return input;
        };
        
        // Normalize shorthand dates
        const normalizedText = normalizeShorthandYear(dateText);
        
        // Date format patterns to try
        const dateFormats = [
            'yyyy-MM-dd',      // ISO format: 2023-12-31
            'MM/dd/yyyy',     // US format: 12/31/2023
            'M/d/yyyy',       // US format with single digits: 2/22/2023
            'dd.MM.yyyy',     // European format: 31.12.2023
            'd.M.yyyy',       // European format with single digits: 1.2.2023
            'dd/MM/yyyy',     // European alt format: 31/12/2023
            'd/M/yyyy',       // European alt with single digits: 1/2/2023
            'MMM d, yyyy',    // Jan 31, 2023
            'd MMM yyyy',     // 31 Jan 2023
        ];
        
        // Try with normalized text first
        if (normalizedText !== dateText) {
            const parsedDate = parse(normalizedText, 'MM/dd/yyyy', new Date());
            if (isValid(parsedDate)) {

                return parsedDate;
            }
        }
        
        // Try all supported formats
        for (const dateFormat of dateFormats) {
            try {
                const parsedDate = parse(dateText, dateFormat, new Date());
                if (isValid(parsedDate)) {

                    return parsedDate;
                }
            } catch (error) {
                // Continue trying other formats
            }
        }
        
        // Try to handle single day number format (e.g., "15" for 15th of current month/year)
        if (/^\d{1,2}$/.test(dateText)) {
            const today = new Date();
            const day = parseInt(dateText, 10);
            if (day >= 1 && day <= 31) {
                const date = new Date(today.getFullYear(), today.getMonth(), day);

                if (isValid(date)) {
                    return date;
                }
            }
        }
        
        // Try to handle month/day format (e.g., "12/25" for December 25th of current year)
        if (/^\d{1,2}\/\d{1,2}$/.test(dateText)) {
            const today = new Date();
            const [month, day] = dateText.split('/').map(Number);
            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                const date = new Date(today.getFullYear(), month - 1, day);

                if (isValid(date)) {
                    return date;
                }
            }
        }
        
        // Try to handle DD.MM format (e.g., 11.2)
        let match: RegExpMatchArray | null = null; // Declare match with let
        match = dateText.match(/^(\d{1,2})\.(\d{1,2})$/); // Reassign match
        if (match) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const today = new Date();
            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                const date = new Date(today.getFullYear(), month - 1, day);

                if (isValid(date)) {
                    return date;
                }
            }
        }
        
        // Try to handle month/day format (e.g., 4/15, 04/15)
        match = dateText.match(/^(\d{1,2})\/(\d{1,2})$/); // Reassign match
        if (match) {
            const month = parseInt(match[1], 10);
            const day = parseInt(match[2], 10);
            const today = new Date();
            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                const date = new Date(today.getFullYear(), month - 1, day);

                if (isValid(date)) {
                    return date;
                }
            }
        }
        

        return null;
    };
    
    // Format a date consistently for display
    const formatDisplayDate = (date: Date): string => {
        return format(date, 'dd.MM.yyyy'); // Use DD.MM.YYYY format
    };
    
    // If the search filter from parent changes, update the input value
    // This handles clearing from parent or external updates
    useEffect(() => {
        if (activeFilters.search !== undefined && activeFilters.search !== inputValue) {
             setInputValue(activeFilters.search || '');
        } else if (activeFilters.search === undefined && inputValue !== ''){
             // Explicitly clear input if parent removes search term
             setInputValue('');
        }
    }, [activeFilters.search]); // Only react to changes in the parent's search filter

    // Using useMemo to avoid recalculating options on every render
    const getOptions = React.useMemo(() => (searchText: string): SearchOption[] => {

        const options: SearchOption[] = [];
        const searchLower = searchText.trim().toLowerCase();
                
        // 1. Try to detect date in search text and add date filter options
        const detectedDate = tryParseDate(searchText);
        if (detectedDate) {

            const dateStr = formatDisplayDate(detectedDate);
            
            // Create date range options (before/after the detected date)
            const startOfDay = new Date(detectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            const endOfDay = new Date(detectedDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            // Exact Date filter: transactions on this exact date
            const exactOption = {
                category: FilterCategory.DATE,
                subcategory: FilterCategory.DATE_EXACT,
                label: t('transactions.filters.search.dateExactLabel', { date: dateStr }),
                value: startOfDay.toISOString() // Store start of day, handler derives end
            };
            options.push(exactOption);

            // Date filter: transactions before this date
            const beforeOption = {
                category: FilterCategory.DATE,
                subcategory: FilterCategory.DATE_BEFORE,
                label: t('transactions.filters.search.dateBeforeLabel', { date: dateStr }),
                value: endOfDay.toISOString()
            };
            options.push(beforeOption);

            // Date filter: transactions after this date
            const afterOption = {
                category: FilterCategory.DATE,
                subcategory: FilterCategory.DATE_AFTER,
                label: t('transactions.filters.search.dateAfterLabel', { date: dateStr }),
                value: startOfDay.toISOString()
            };
            options.push(afterOption);

        }
        

        if (!searchLower || searchLower.length >= 2) {
            // Add transaction types (all or filtered)
            const availableTypes = (transactionTypes || []);
            const matchingTypes = !searchLower ? 
                availableTypes :
                availableTypes.filter((type: { name: string, value: TransactionTypeValue }) => type.name.toLowerCase().includes(searchLower)).slice(0, 5);
                
            matchingTypes.forEach(type => {
                options.push({
                    category: FilterCategory.TYPE,
                    label: type.name,
                    value: type.value,
                });
            });
            
            // Add accounts (all or filtered)
            const availableAccounts = (accounts || []);
            const matchingAccounts = !searchLower ? 
                availableAccounts.slice(0, 10) :
                availableAccounts.filter((account: string) => account.toLowerCase().includes(searchLower)).slice(0, 3);
                
            matchingAccounts.forEach(account => {
                options.push({
                    category: FilterCategory.ACCOUNT,
                    label: account,
                    value: account,
                });
            });
            
            // Add assets (all or filtered)
            const availableAssets = (assets || []);
            const matchingAssets = !searchLower ? 
                availableAssets.slice(0, 10) :
                availableAssets.filter((asset: { name: string, ticker: string }) => {
                    const nameLower = asset.name.toLowerCase();
                    const tickerLower = asset.ticker.toLowerCase();
                    return nameLower.includes(searchLower) || tickerLower.includes(searchLower);
                }).slice(0, 3);
                
            matchingAssets.forEach(asset => {
                options.push({
                    category: FilterCategory.ASSET,
                    label: `${asset.name} (${asset.ticker})`,
                    value: asset.ticker,
                });
            });
            
            // Add issue types (all or filtered)
            const availableIssues = (transactionIssues || []);
            const matchingIssues = !searchLower ? 
                availableIssues :
                availableIssues.filter((issue: { description: string, id: string }) => issue.description.toLowerCase().includes(searchLower)).slice(0, 3);
                
            matchingIssues.forEach(issue => {
                options.push({
                    category: FilterCategory.FLAG,
                    label: issue.description,
                    value: issue.id,
                });
            });
        }
        
        if (searchLower) {
            options.push({
                category: FilterCategory.TEXT,
                label: searchLower,
                value: searchLower,
            });
        }

        return options;
    }, [t, transactionTypes, accounts, assets]); 

    // --- Derive selected options from activeFilters for the Autocomplete value prop ---
    const selectedOptions = useMemo((): SearchOption[] => {
        const derivedOptions: SearchOption[] = [];
        
        // Helper to find an option by value and category
        const findOption = (category: FilterCategory, value: any, source: any[], labelKey: string, valueKey: string | ((item: any) => any)): SearchOption | undefined => {
            const item = source?.find(i => (typeof valueKey === 'function' ? valueKey(i) : i[valueKey]) === value);
            if (item) {
                return {
                    category,
                    label: item[labelKey],
                    value: (typeof valueKey === 'function' ? valueKey(item) : item[valueKey]),
                };
            }
            return undefined;
        };

        // Find selected Types
        (activeFilters.types || []).forEach(value => {
            const option = findOption(FilterCategory.TYPE, value, transactionTypes || [], 'name', 'value');
            if (option) derivedOptions.push(option);
        });

        // Find selected Accounts
        (activeFilters.accounts || []).forEach(value => {
             // For accounts, the value IS the label
             if (accounts?.includes(value)) { // Check if account still exists
                 derivedOptions.push({
                    category: FilterCategory.ACCOUNT,
                    label: value,
                    value: value,
                 });
             }
        });

        // Find selected Assets
        (activeFilters.assets || []).forEach(value => {
            const asset = (assets || []).find(a => a.ticker === value);
            if (asset) {
                derivedOptions.push({
                    category: FilterCategory.ASSET,
                    label: `${asset.name} (${asset.ticker})`,
                    value: asset.ticker,
                });
            }
        });

        // Find selected Flags
        (activeFilters.flags || []).forEach(value => {
            const option = findOption(FilterCategory.FLAG, value, transactionIssues || [], 'description', 'id');
            if (option) derivedOptions.push(option);
        });

        // Note: 'search' and 'dates' are not represented as chips, so not included here
        
        return derivedOptions;
    }, [activeFilters, transactionTypes, accounts, assets, transactionIssues]); // Re-run when activeFilters or data sources change

    const handleOptionChange = (
        _: SyntheticEvent,
        newValue: (string | SearchOption)[], 
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<SearchOption> 
    ) => {
        let filterUpdateInstruction: FilterUpdatePayload = {};
 
        if (reason === 'clear') {
            filterUpdateInstruction = {
                search: undefined,
                types: { clear: true },
                accounts: { clear: true },
                assets: { clear: true },
                flags: { clear: true },
                dates: { clear: true }
            };
            setInputValue('');
 
        } else if (reason === 'selectOption' && details?.option) {
            const { category, value } = details.option;
            switch (category) {
                case FilterCategory.TYPE:
                    filterUpdateInstruction = { types: { add: [value as TransactionTypeValue] } };
                    break;
                case FilterCategory.ACCOUNT:
                    filterUpdateInstruction = { accounts: { add: [value.toString()] } };
                    break;
                case FilterCategory.ASSET:
                    filterUpdateInstruction = { assets: { add: [value.toString()] } };
                    break;
                case FilterCategory.FLAG:
                    filterUpdateInstruction = { flags: { add: [value.toString()] } };
                    break;
                case FilterCategory.TEXT:
                    filterUpdateInstruction = { search: value.toString() };
                    break;
                case FilterCategory.DATE:
                    // Date filters typically replace, not add/remove array items
                    let newStartDate: string | null | undefined = undefined;
                    let newEndDate: string | null | undefined = undefined;  
                    if (details.option.subcategory === FilterCategory.DATE_BEFORE) {
                        newEndDate = value.toString();
                    } else if (details.option.subcategory === FilterCategory.DATE_AFTER) {
                        newStartDate = value.toString();
                    } else if (details.option.subcategory === FilterCategory.DATE_EXACT) {
                        const selectedStart = new Date(value.toString());
                        const selectedEnd = new Date(selectedStart);
                        selectedEnd.setHours(23, 59, 59, 999);
                        newStartDate = selectedStart.toISOString();
                        newEndDate = selectedEnd.toISOString();
                    }
                    // Send direct update for dates object
                    filterUpdateInstruction = { dates: { start: newStartDate ?? null, end: newEndDate ?? null } }; // Use null coalescing
                    break;
            }
 
        } else if (reason === 'removeOption' && details?.option) {
            const { category, value } = details.option;
            switch (category) {
                case FilterCategory.TYPE:
                    filterUpdateInstruction = { types: { remove: [value as TransactionTypeValue] } };
                    break;
                case FilterCategory.ACCOUNT:
                    filterUpdateInstruction = { accounts: { remove: [value.toString()] } };
                    break;
                case FilterCategory.ASSET:
                    filterUpdateInstruction = { assets: { remove: [value.toString()] } };
                    break;
                case FilterCategory.FLAG:
                    filterUpdateInstruction = { flags: { remove: [value.toString()] } };
                    break;
            }
        }

        if (Object.keys(filterUpdateInstruction).length > 0) {
            onFiltersChange(filterUpdateInstruction);
        }
    };

    const handleInputChange = (
        _event: React.SyntheticEvent | null,
        newValue: string, 
        _reason: AutocompleteInputChangeReason
    ) => {
        setInputValue(newValue);
    };

    const handleBlur = () => {
        setInputValue('');
    };

    return (
        <Box sx={{ flex: 0.9, minWidth: '200px' }}>
            <Autocomplete
                freeSolo
                clearOnBlur={false}
                multiple
                disableCloseOnSelect
                value={selectedOptions} 
                options={getOptions(inputValue)}
                open={isDropdownOpen}
                onOpen={() => {
                    setIsDropdownOpen(true);
                    if (onDropdownStateChange) onDropdownStateChange(true);
                }}
                onClose={() => {
                    setIsDropdownOpen(false);
                    if (onDropdownStateChange) onDropdownStateChange(false);
                }}
                groupBy={(option) => {
                    const categoryStr = String(option.category);

                    return categoryStr;
                }}
                getOptionLabel={(option) => {

                    // Handle both string values and option objects
                    if (typeof option === 'string') return option;
                    return option.label;
                }}
                isOptionEqualToValue={(option, value) => {
                    // Handle case where value might be a string (freeSolo initial value)
                    if (typeof value === 'string') {
                        return false; // A string value won't match an option object
                    }
                    // Compare based on category and value for object options
                    return option.category === value.category && option.value === value.value;
                }}
                onChange={handleOptionChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onBlur={handleBlur} 
                renderTags={() => null}
                filterOptions={(options, params) => {
                    const inputValue = params.inputValue.trim().toLowerCase();

                    if (!inputValue) {
                        return options;
                    }

                    // Check if the input is a date
                    const parsedDate = tryParseDate(params.inputValue); // Use original case for parsing
                    const isDateInput = parsedDate !== null;


                    const filtered: SearchOption[] = [];
                    options.forEach(option => {
                        // Always include DATE options if the input is a date
                        if (option.category === FilterCategory.DATE && isDateInput) {

                            filtered.push(option);
                            return; // Move to next option
                        }

                        // For non-date options OR if input is not a date, filter by label
                        if (option.label.toLowerCase().includes(inputValue)) {

                            filtered.push(option);
                        }
                    });


                    return filtered;
                }}
                renderGroup={(params) => {
                    let groupLabel = '';
                    const group = String(params.group) as FilterCategory;
                    
                    switch (group) {
                        case FilterCategory.TEXT:
                            groupLabel = t('transactions.filters.searchGroupHeader'); 
                            break;
                        case FilterCategory.TYPE:
                            groupLabel = t('transactions.filters.type.label');
                            break;
                        case FilterCategory.ACCOUNT:
                            groupLabel = t('transactions.filters.search.accountCategory');
                            break;
                        case FilterCategory.ASSET:
                            groupLabel = t('transactions.filters.search.assetCategory');
                            break;
                        case FilterCategory.FLAG:
                            groupLabel = t('transactions.filters.search.statusCategory');
                            break;
                        case FilterCategory.DATE:
                            groupLabel = t('transactions.filters.search.dateCategory');
                            break;
                    }
                    
                    // Only render groups that have items
                    if (!params.children || React.Children.count(params.children) === 0) {
                        return null;
                    }
                    
                    return (
                        <Box key={params.key}>
                            {group !== FilterCategory.TEXT && <Divider />}
                            <Typography 
                                variant="caption" 
                                sx={{
                                    px: 2,
                                    py: 0.5,
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontWeight: 700,
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                                }}
                            >
                                {groupLabel}
                            </Typography>
                            {params.children}
                        </Box>
                    );
                }}
                renderOption={(props, option) => {

                    // Extract the key from props to pass it separately
                    const { key, ...otherProps } = props;
                    
                    return (
                        <Box component="li" key={key} {...otherProps} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 87, 34, 0.08)',
                            }
                        }}>
                            <Box sx={{ fontWeight: 500 }}>{option.label}</Box>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={t('transactions.filters.searchPlaceholder')}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <SearchIcon sx={{ color: 'action.active', fontSize: '20px', mr: 1 }} />
                                    {params.InputProps.startAdornment}
                                </>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default Search;
